import type { TOC } from '@ember/component/template-only'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { t } from 'ember-intl'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { AlertType, InlineAlert } from 'district-ui-client/components/inline-alert'
import { ThemedLinkTo } from 'district-ui-client/components/themed-button'
import { PanelTitle, Tile } from 'district-ui-client/components/section'
import { FeatureNotificationId } from 'district-ui-client/services/feature-notification'
import { FeatureNotification } from 'district-ui-client/helpers/feature-notification'
import type ActiveRouteService from 'district-ui-client/services/active-route'
import { type ReportingLink } from 'district-ui-client/services/active-route'
import { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type FeaturesService from 'district-ui-client/services/features'
import type ReportRegistryService from 'district-ui-client/services/report-registry'
import {
  MathseedsReportPath,
  MathleticsReportPath,
  ReadingEggsReportPath,
} from 'district-ui-client/services/report-registry'
import type District from 'district-ui-client/models/district'
import { assert } from '@ember/debug'
import { trackedFunction } from 'reactiveweb/function'
import type Store from '@ember-data/store'
import type SessionService from 'district-ui-client/services/session'
import type School from 'district-ui-client/models/school'
import { OverCapacityAlert } from 'district-ui-client/components/over-capacity-alert'
import featureFlag from 'district-ui-client/helpers/feature-flag'
import { SubscriptionSummaryHeader } from 'district-ui-client/components/subscription-summary-header'
import { eq } from 'ember-truth-helpers'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import { Lozenge } from 'district-ui-client/components/lozenge'
import { on } from '@ember/modifier'

interface HomePageSignature {
  Element: HTMLDivElement
  Args: {
    district: District & { id: string }
  }
}

export class HomePage extends Component<HomePageSignature> {
  @service activeRoute!: ActiveRouteService

  @service session!: SessionService

  @service store!: Store

  @service externalRostering!: ExternalRosteringService

  @service features!: FeaturesService

  get subscriptionType(): SubscriptionType {
    assert('HomePage component route must have a subscription type', this.activeRoute.subscriptionType)
    return this.activeRoute.subscriptionType
  }

  get showExternalRosteringSection() {
    return this.externalRostering.isEnabled
  }

  get showManageSection() {
    const manageAvailable =
      this.subscriptionType !== SubscriptionType.Mathletics ||
      (Boolean(this.args.district.oneRosterEnabled) && this.features.isEnabled('dui_rostering_improvements_2026'))
    return manageAvailable
  }

  fetchSchools = trackedFunction(this, async () => {
    const districtId = this.session.currentDistrict.id
    await Promise.resolve() // disconnect tracking before store calls

    return await this.store.query<School>('school', {
      scope: `districts/${districtId}`,
      // @ts-expect-error - include types not correct
      include: 'school-subscription-utilisations',
    })
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <div class="flex flex-col space-y-6" ...attributes>
      <span class="text-2xl font-medium">{{t "welcomeDistrict" districtName=@district.name}}</span>
      <SubscriptionSummaryHeader @districtId={{@district.id}} @subscriptionType={{this.subscriptionType}} />
      <OverCapacityAlert @schools={{this.fetchSchools.value}} @subscriptionType={{this.subscriptionType}} />
      {{#if (FeatureNotification FeatureNotificationId.SetAcademicStandards)}}
        <SelectStateStandardsAlert />
      {{/if}}
      <ReportsSection @districtId={{@district.id}} />
      {{#if this.showExternalRosteringSection}}
        <ExternalRosteringSection @subscriptionType={{this.subscriptionType}} />
      {{else if this.showManageSection}}
        <ManageSection @oneRosterEnabled={{@district.oneRosterEnabled}} />
      {{/if}}
    </div>
  </template>
}

const SelectStateStandardsAlert: TOC<{
  Element: HTMLDivElement
}> = <template>
  <InlineAlert @type={{AlertType.Warning}} data-test-no-standards-alert ...attributes>
    <div class="space-y-2">
      <h2 class="m-0 text-sm font-medium">{{t "stateAcademicStandards.alertTitle"}}</h2>
      <p class="m-0 text-sm">{{t "stateAcademicStandards.alertDescription"}}</p>
      <ThemedLinkTo @style="theme" @route="settings.district-settings">
        <span>{{t "stateAcademicStandards.alertButton"}}</span>
        <PhosphorIcon @icon="phosphor-arrow-right" />
      </ThemedLinkTo>
    </div>
  </InlineAlert>
</template>

interface ReportsSectionSignature {
  Args: {
    districtId: string
  }
}

class ReportsSection extends Component<ReportsSectionSignature> {
  @service activeRoute!: ActiveRouteService

  @service reportRegistry!: ReportRegistryService

  get reportingStandardsRoute(): ReportingLink | undefined {
    const models = ['district', this.args.districtId]
    const query = { dateRange: undefined, studentGrade: undefined, contentLevel: undefined, schoolIds: undefined }
    switch (this.activeRoute.subscriptionType) {
      case SubscriptionType.Reading:
        return this.reportRegistry.isReportEnabled(ReadingEggsReportPath.StandardsPerformance)
          ? { route: ReadingEggsReportPath.StandardsPerformance, models, query }
          : undefined
      case SubscriptionType.Maths:
        return this.reportRegistry.isReportEnabled(MathseedsReportPath.StandardsPerformance)
          ? { route: MathseedsReportPath.StandardsPerformance, models, query }
          : undefined
      case SubscriptionType.Mathletics:
        return this.reportRegistry.isReportEnabled(MathleticsReportPath.StandardsPerformance)
          ? { route: MathleticsReportPath.StandardsPerformance, models, query }
          : undefined
      default:
        return
    }
  }

  get reportingDashboardRoute(): ReportingLink | undefined {
    const models = ['district', this.args.districtId]
    const query = { dateRange: undefined, studentGrade: undefined, contentLevel: undefined, schoolIds: undefined }
    switch (this.activeRoute.subscriptionType) {
      case SubscriptionType.Reading:
        return { route: ReadingEggsReportPath.Dashboard, models, query }
      case SubscriptionType.Maths:
        return { route: MathseedsReportPath.Dashboard, models, query }
      default:
        return
    }
  }

  <template>
    <section class="space-y-2">
      <span class="text-2xl text-neutral-400">{{t "components.homePage.reports"}}</span>
      <div class="flex flex-wrap gap-3">
        {{#if this.reportingStandardsRoute}}
          <HomeTile
            data-test-standards-report-tile
            class="min-h-52 flex-shrink flex-grow basis-96"
            @title={{t "components.homePage.standardsPerformance"}}
            @route={{this.reportingStandardsRoute.route}}
            @models={{this.reportingStandardsRoute.models}}
            @query={{this.reportingStandardsRoute.query}}
          >
            <:icon>
              <PhosphorIcon @icon="phosphor-chart-line-bold" class="h-8 w-8 text-wl-blue-150" />
            </:icon>
            <:default>
              <ul class="ml-7 mt-3 list-outside list-disc">
                <li>{{t "components.homePage.standardsPerformance1"}}</li>
                <li>{{t "components.homePage.standardsPerformance2"}}</li>
              </ul>
            </:default>
          </HomeTile>
        {{/if}}
        {{#if this.reportingDashboardRoute}}
          <HomeTile
            data-test-dashboard-report-tile
            class="min-h-52 flex-shrink flex-grow basis-96"
            @title={{t "components.homePage.reportsDashboard"}}
            @route={{this.reportingDashboardRoute.route}}
            @models={{this.reportingDashboardRoute.models}}
            @query={{this.reportingDashboardRoute.query}}
          >
            <:icon>
              <PhosphorIcon @icon="phosphor-chart-pie-slice-fill" class="h-8 w-8 text-wl-blue-150" />
            </:icon>
            <:default>
              <ul class="ml-7 mt-3 list-outside list-disc">
                <li>{{t "components.homePage.reportsDashboardOverview"}}</li>
              </ul>
            </:default>
          </HomeTile>
        {{/if}}
      </div>
    </section>
  </template>
}

const ManageSection: TOC<{ Args: { oneRosterEnabled?: boolean } }> = <template>
  <section class="space-y-2">
    <span class="text-2xl text-neutral-400">{{t "components.homePage.manage"}}</span>
    {{#if (featureFlag "dui_rostering_improvements_2026")}}
      <div class="grid grid-cols-2 gap-3">
        {{#if @oneRosterEnabled}}
          <HomeTile
            data-test-manage-roster-tile
            class="min-h-48"
            @title={{t "components.homePage.manageRoster"}}
            @route="subscription-type.manage.roster"
            @hideCornerHighlight={{true}}
          >
            <:icon>
              <PhosphorIcon @icon="phosphor-user-list-fill" class="ml-1 h-7 w-7 text-wl-blue-150" />
            </:icon>
            <:afterTitle>
              <Lozenge data-test-manage-roster-new-pill class="shrink-0 grow-0 px-2">{{t "new"}}</Lozenge>
            </:afterTitle>
            <:default>
              <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
                <li>{{t "components.homePage.manageRoster1"}}</li>
                <li>{{t "components.homePage.manageRoster2"}}</li>
              </ul>
            </:default>
          </HomeTile>
        {{/if}}
        <HomeTile
          data-test-manage-schools-tile
          class="min-h-48"
          @title={{t "components.homePage.manageSchools"}}
          @route="subscription-type.manage.schools"
          @hideCornerHighlight={{true}}
        >
          <:icon>
            <PhosphorIcon @icon="phosphor-building-apartment-fill" class="ml-1 h-7 w-7 text-wl-blue-150" />
          </:icon>
          <:default>
            <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
              <li>{{t "components.homePage.manageSchoolsNew1"}}</li>
              <li>{{t "components.homePage.manageSchoolsNew2"}}</li>
            </ul>
          </:default>
        </HomeTile>
        <HomeTile
          data-test-manage-teachers-tile
          class="min-h-48"
          @title={{t "components.homePage.manageTeachers"}}
          @route="subscription-type.manage.teachers"
          @hideCornerHighlight={{true}}
        >
          <:icon>
            <PhosphorIcon @icon="phosphor-chalkboard-teacher-fill" class="ml-1 h-7 w-7 text-wl-blue-150" />
          </:icon>
          <:default>
            <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
              <li>{{t "components.homePage.manageTeachersNew1"}}</li>
              <li>{{t "components.homePage.manageTeachersNew2"}}</li>
            </ul>
          </:default>
        </HomeTile>
        <HomeTile
          data-test-manage-students-tile
          class="min-h-48"
          @title={{t "components.homePage.manageStudents"}}
          @route="subscription-type.manage.students"
          @hideCornerHighlight={{true}}
        >
          <:icon>
            <PhosphorIcon @icon="phosphor-users-three-fill" class="ml-1 h-7 w-7 text-wl-blue-150" />
          </:icon>
          <:default>
            <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
              <li>{{t "components.homePage.manageStudentsNew1"}}</li>
              <li>{{t "components.homePage.manageStudentsNew2"}}</li>
              <li>{{t "components.homePage.manageStudentsNew3"}}</li>
            </ul>
          </:default>
        </HomeTile>
      </div>
    {{else}}
      <div class="flex flex-wrap gap-3">
        <HomeTile
          data-test-manage-schools-tile
          class="min-h-48 w-1/3 flex-shrink flex-grow basis-96"
          @title={{t "components.homePage.manageSchools"}}
          @route="subscription-type.manage.schools"
        >
          <:icon>
            <PhosphorIcon @icon="phosphor-building-apartment-fill" class="ml-1 h-7 w-7 text-wl-blue-150" />
          </:icon>
          <:default>
            <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
              <li>{{t "components.homePage.manageSchools1"}}</li>
              <li>{{t "components.homePage.manageSchools2"}}</li>
            </ul>
          </:default>
        </HomeTile>
        <HomeTile
          data-test-manage-teachers-tile
          class="min-h-48 w-1/3 flex-shrink flex-grow basis-96"
          @title={{t "components.homePage.manageTeachers"}}
          @route="subscription-type.manage.teachers"
        >
          <:icon>
            <PhosphorIcon @icon="phosphor-chalkboard-teacher-fill" class="ml-1 h-7 w-7 text-wl-blue-150" />
          </:icon>
          <:default>
            <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
              <li>{{t "components.homePage.manageTeachers1"}}</li>
              <li>{{t "components.homePage.manageTeachers2"}}</li>
              <li>{{t "components.homePage.manageTeachers3"}}</li>
            </ul>
          </:default>
        </HomeTile>
        <HomeTile
          data-test-manage-students-tile
          class="min-h-48 w-1/3 flex-shrink flex-grow basis-96"
          @title={{t "components.homePage.manageStudents"}}
          @route="subscription-type.manage.students"
        >
          <:icon>
            <PhosphorIcon @icon="phosphor-users-three-fill" class="ml-1 h-7 w-7 text-wl-blue-150" />
          </:icon>
          <:default>
            <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
              <li>{{t "components.homePage.manageStudents1"}}</li>
              <li>{{t "components.homePage.manageStudents2"}}</li>
              <li>{{t "components.homePage.manageStudents3"}}</li>
              <li>{{t "components.homePage.manageStudents4"}}</li>
            </ul>
          </:default>
        </HomeTile>
      </div>
    {{/if}}
  </section>
</template>

class ExternalRosteringSection extends Component<{
  Args: {
    subscriptionType: SubscriptionType
  }
  Element: HTMLElement
}> {
  @service externalRostering!: ExternalRosteringService

  get isEnabled() {
    return this.externalRostering.isEnabled && (Boolean(this.usageSummaryUrl) || Boolean(this.manageUrl))
  }

  get manageUrl(): string | undefined {
    return this.externalRostering.manageUrl(this.args.subscriptionType)
  }

  get usageSummaryUrl(): string | undefined {
    return this.externalRostering.usageSummaryUrl(this.args.subscriptionType)
  }

  <template>
    {{#if this.isEnabled}}
      <section class="space-y-2">
        <span class="text-2xl text-neutral-400">{{this.externalRostering.displayName}}</span>
        <div class="flex flex-wrap gap-3 whitespace-nowrap">
          {{#if this.usageSummaryUrl}}
            <UsageSummaryTile @usageSummaryUrl={{this.usageSummaryUrl}} />
          {{/if}}
          {{#if this.manageUrl}}
            <ManageRosterTile @manageUrl={{this.manageUrl}} />
          {{/if}}
        </div>
      </section>
    {{/if}}
  </template>
}

const HomeTile: TOC<{
  Element: HTMLElement
  Args: {
    title: string
    route?: string
    models?: unknown[]
    query?: Record<string, unknown>
    href?: string
    hideCornerHighlight?: boolean
  }
  Blocks: {
    default: []
    icon: []
    afterTitle: []
  }
}> = <template>
  <Tile
    data-test-home-tile
    @href={{@href}}
    @route={{@route}}
    @models={{@models}}
    @query={{@query}}
    class="group relative min-h-[172px] flex-shrink-0 overflow-hidden"
    ...attributes
  >
    <div class="flex items-center space-x-3">
      {{yield to="icon"}}<PanelTitle>{{@title}}</PanelTitle>{{yield to="afterTitle"}}
    </div>
    {{yield}}
    <div
      class="absolute bottom-0 right-0 h-[72px] w-[72px] transform-gpu rounded-tl-full transition-colors duration-500 ease-in-out
        {{unless @hideCornerHighlight 'group-hover:bg-blue-100 group-focus:bg-blue-100'}}"
    >
      <PhosphorIcon
        @icon="phosphor-arrow-right-bold"
        class="absolute bottom-3.5 right-5 h-[22px] w-[22px] text-blue-325"
      />
    </div>
  </Tile>
</template>

class ManageRosterTile extends Component<{
  Element: HTMLElement
  Args: {
    manageUrl: string
  }
}> {
  @service externalRostering!: ExternalRosteringService

  logManageUrlClick = () => {
    this.externalRostering.logManageUrlClick()
  }

  <template>
    <HomeTile
      data-test-external-rostering-review-tile={{this.externalRostering.source}}
      class="h-36 min-w-min flex-shrink flex-grow basis-0"
      @title={{t "components.homePage.externalRosteringReviewTile.title"}}
      @href={{@manageUrl}}
      {{on "click" this.logManageUrlClick}}
    >
      <:icon>
        <PhosphorIcon @icon="phosphor-users-fill" class="h-8 w-8 text-wl-blue-150" />
      </:icon>
      <:afterTitle>
        {{#if (FeatureNotification FeatureNotificationId.ManageIntegration)}}
          <Lozenge class="shrink-0 grow-0 px-2">{{t "new"}}</Lozenge>
        {{/if}}
      </:afterTitle>
      <:default>
        <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
          {{#if (eq this.externalRostering.source "wonde")}}
            <WondeBulletPoints />
          {{else if (eq this.externalRostering.source "classlink")}}
            <ClassLinkBulletPoints />
          {{else if (eq this.externalRostering.source "clever")}}
            <CleverBulletPoints />
          {{else}}
            <li>{{t
                "components.homePage.externalRosteringReviewTile.description"
                name=this.externalRostering.displayName
              }}</li>
          {{/if}}
        </ul>
      </:default>
    </HomeTile>
  </template>
}

class UsageSummaryTile extends Component<{
  Element: HTMLElement
  Args: {
    usageSummaryUrl: string
  }
}> {
  @service externalRostering!: ExternalRosteringService

  logUsageSummaryUrlClick = () => {
    this.externalRostering.logUsageSummaryUrlClick()
  }

  <template>
    <HomeTile
      data-test-external-rostering-tile={{this.externalRostering.source}}
      class="h-36 min-w-min flex-shrink flex-grow basis-0"
      @title={{t "components.homePage.externalRosteringTile.title"}}
      @href={{@usageSummaryUrl}}
      {{on "click" this.logUsageSummaryUrlClick}}
    >
      <:icon>
        <PhosphorIcon @icon="phosphor-user-list-fill" class="h-8 w-8 text-wl-blue-150" />
      </:icon>
      <:afterTitle>
        {{#if (FeatureNotification FeatureNotificationId.IntegrationUsageSummary)}}
          <Lozenge class="shrink-0 grow-0 px-2">{{t "new"}}</Lozenge>
        {{/if}}
      </:afterTitle>
      <:default>
        <ul class="ml-7 mt-3 list-outside list-disc space-y-1.5">
          <li>{{t "components.homePage.externalRosteringTile.description" name=this.externalRostering.displayName}}</li>
        </ul>
      </:default>
    </HomeTile>
  </template>
}

const WondeBulletPoints: TOC<unknown> = <template>
  <li>{{t "components.homePage.externalRosteringReviewTile.wonde.bullet1"}}</li>
  <li>{{t "components.homePage.externalRosteringReviewTile.wonde.bullet2"}}</li>
  <li>{{t "components.homePage.externalRosteringReviewTile.wonde.bullet3"}}</li>
</template>

const ClassLinkBulletPoints: TOC<unknown> = <template>
  <li>{{t "components.homePage.externalRosteringReviewTile.classlink.bullet1"}}</li>
  <li>{{t "components.homePage.externalRosteringReviewTile.classlink.bullet2"}}</li>
  <li>{{t "components.homePage.externalRosteringReviewTile.classlink.bullet3"}}</li>
</template>

const CleverBulletPoints: TOC<unknown> = <template>
  <li>{{t "components.homePage.externalRosteringReviewTile.clever.bullet1"}}</li>
  <li>{{t "components.homePage.externalRosteringReviewTile.clever.bullet2"}}</li>
  <li>{{t "components.homePage.externalRosteringReviewTile.clever.bullet3"}}</li>
</template>

export default HomePage
